import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  Shield,
  BarChart2,
  Puzzle,
  Users,
  Headphones,
  Star,
  ChevronDown,
  ChevronUp,
  Check,
  ArrowRight,
} from "../utils/icons";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Avatar from "../components/ui/Avatar";
import { features, pricingPlans, testimonials, faqs } from "../utils/dummyData";

// ─── Icon Map ─────────────────────────────────────────────────────────────────
const iconMap = { Zap, Shield, BarChart2, Puzzle, Users, Headphones };

// ─── Hero ─────────────────────────────────────────────────────────────────────
const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background orbs */}
      <div className="orb w-96 h-96 bg-primary-500 top-20 -left-32" />
      <div className="orb w-80 h-80 bg-accent-500 bottom-20 -right-20" />
      <div className="orb w-64 h-64 bg-violet-500 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(99,102,241,1) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="relative max-container section-pad text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-full px-4 py-1.5 mb-8 animate-fade-up">
          <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse-slow" />
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
            Now in Public Beta — Free for 14 days
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight animate-fade-up stagger-1">
          The Platform That
          <br />
          <span className="gradient-text">Powers Modern Teams</span>
        </h1>

        <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-10 animate-fade-up stagger-2 leading-relaxed">
          NexaFlow gives your team analytics, automation, and collaboration
          tools in one beautiful dashboard. Ship faster, grow smarter.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up stagger-3 mb-16">
          <Button
            size="lg"
            onClick={() => navigate("/signup")}
            icon={<ArrowRight size={18} />}
          >
            Start Free Trial
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate("/dashboard")}
          >
            View Live Demo
          </Button>
        </div>

        {/* Social proof */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-up stagger-4">
          <div className="flex -space-x-2">
            {[
              "Aria Chen",
              "Marcus Webb",
              "Sofia Reyes",
              "Ethan Park",
              "Luna Patel",
            ].map((n) => (
              <Avatar
                key={n}
                name={n}
                size="sm"
                className="ring-2 ring-white dark:ring-gray-900"
              />
            ))}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-1 text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} filled className="text-amber-400" />
              ))}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Trusted by{" "}
              <strong className="text-gray-700 dark:text-gray-300">
                1,780+
              </strong>{" "}
              teams worldwide
            </p>
          </div>
        </div>

        {/* Dashboard preview */}
        <div className="mt-20 relative animate-fade-up stagger-5">
          <div className="relative mx-auto max-w-5xl">
            <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-surface-900 via-transparent to-transparent z-10 rounded-2xl" />
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 shadow-2xl border border-gray-700 ring-1 ring-white/10">
              {/* Mock browser bar */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <div className="flex-1 mx-4 h-6 bg-gray-700 rounded-md" />
              </div>
              {/* Mock stat cards */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {["$54K MRR", "1,780 Users", "4.2% Churn", "98.9% Uptime"].map(
                  (stat, i) => (
                    <div
                      key={i}
                      className="bg-gray-800 rounded-xl p-3 border border-gray-700"
                    >
                      <div className="h-2 bg-gray-600 rounded w-2/3 mb-2" />
                      <div className="text-white text-sm font-bold font-display">
                        {stat}
                      </div>
                    </div>
                  ),
                )}
              </div>
              {/* Mock charts */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 bg-gray-800 rounded-xl p-3 border border-gray-700 h-32 flex items-end gap-1">
                  {[40, 65, 45, 80, 60, 90, 75, 95, 70, 100, 85, 120].map(
                    (h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-sm"
                        style={{
                          height: `${h * 0.9}%`,
                          background:
                            i === 11 ? "#6366f1" : "rgba(99,102,241,0.35)",
                        }}
                      />
                    ),
                  )}
                </div>
                <div className="bg-gray-800 rounded-xl p-3 border border-gray-700 h-32 flex flex-col justify-center items-center gap-2">
                  <div
                    className="w-16 h-16 rounded-full border-4 border-primary-500 border-t-transparent animate-spin"
                    style={{ animationDuration: "3s" }}
                  />
                  <div className="h-2 bg-gray-600 rounded w-3/4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Features ─────────────────────────────────────────────────────────────────
const Features = () => (
  <section id="features" className="section-pad bg-gray-50 dark:bg-gray-900/30">
    <div className="max-container">
      <div className="text-center mb-16">
        <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-3">
          Everything You Need
        </p>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          Built for the way{" "}
          <span className="gradient-text">teams actually work</span>
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-lg">
          Six powerful capabilities, unified in one platform. No duct tape
          required.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feat, i) => {
          const IconComp = iconMap[feat.icon];
          return (
            <Card
              key={i}
              hover
              className={`p-6 animate-fade-up stagger-${Math.min(i + 1, 6)}`}
            >
              <div
                className={`w-11 h-11 rounded-xl ${feat.bg} flex items-center justify-center mb-4`}
              >
                {IconComp && <IconComp size={22} className={feat.color} />}
              </div>
              <h3 className="font-display font-bold text-lg text-gray-900 dark:text-white mb-2">
                {feat.title}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                {feat.description}
              </p>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

// ─── Pricing ──────────────────────────────────────────────────────────────────
const Pricing = () => {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="section-pad">
      <div className="max-container">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-3">
            Pricing
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Simple, <span className="gradient-text">transparent pricing</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            No hidden fees. Cancel anytime.
          </p>

          {/* Annual/Monthly toggle */}
          <div className="inline-flex items-center gap-3 bg-gray-100 dark:bg-gray-800 rounded-full p-1">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                !annual
                  ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white"
                  : "text-gray-500"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                annual
                  ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white"
                  : "text-gray-500"
              }`}
            >
              Annual{" "}
              <span className="ml-1.5 text-xs bg-emerald-500 text-white rounded-full px-2 py-0.5">
                -20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pricingPlans.map((plan, i) => (
            <div
              key={i}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                plan.highlight
                  ? "bg-gradient-to-br from-primary-600 to-violet-600 text-white shadow-glow scale-105"
                  : "card hover:shadow-lg hover:-translate-y-1"
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold px-4 py-1.5 rounded-full">
                  MOST POPULAR
                </div>
              )}

              <p
                className={`text-sm font-semibold mb-2 ${
                  plan.highlight
                    ? "text-primary-200"
                    : "text-primary-600 dark:text-primary-400"
                }`}
              >
                {plan.name}
              </p>

              <div className="flex items-end gap-1 mb-3">
                <span
                  className={`font-display text-5xl font-bold ${
                    plan.highlight
                      ? "text-white"
                      : "text-gray-900 dark:text-white"
                  }`}
                >
                  ${annual ? Math.round(plan.price * 0.8) : plan.price}
                </span>
                <span
                  className={`text-sm mb-2 ${
                    plan.highlight ? "text-primary-200" : "text-gray-400"
                  }`}
                >
                  /mo
                </span>
              </div>

              <p
                className={`text-sm mb-6 ${
                  plan.highlight
                    ? "text-primary-100"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {plan.description}
              </p>

              <ul className="space-y-3 mb-8">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm">
                    <Check
                      size={16}
                      className={`mt-0.5 flex-shrink-0 ${
                        plan.highlight ? "text-white" : "text-emerald-500"
                      }`}
                    />
                    <span
                      className={
                        plan.highlight
                          ? "text-primary-100"
                          : "text-gray-600 dark:text-gray-400"
                      }
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                variant={plan.highlight ? "subtle" : "primary"}
                className={`w-full ${
                  plan.highlight
                    ? "!bg-white !text-primary-700 hover:!bg-primary-50"
                    : ""
                }`}
                onClick={() => navigate("/signup")}
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Testimonials ─────────────────────────────────────────────────────────────
const Testimonials = () => (
  <section
    id="testimonials"
    className="section-pad bg-gray-50 dark:bg-gray-900/30"
  >
    <div className="max-container">
      <div className="text-center mb-16">
        <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-3">
          Testimonials
        </p>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white">
          Loved by <span className="gradient-text">1,780+ teams</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.map((t, i) => (
          <Card key={i} className="p-7 flex flex-col gap-4">
            <div className="flex gap-1 text-amber-400">
              {[...Array(t.rating)].map((_, j) => (
                <Star key={j} size={14} filled className="text-amber-400" />
              ))}
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed italic flex-1">
              "{t.quote}"
            </p>
            <div className="flex items-center gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <Avatar name={t.name} size="md" />
              <div>
                <p className="font-semibold text-sm text-gray-900 dark:text-white">
                  {t.name}
                </p>
                <p className="text-xs text-gray-500">{t.role}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// ─── FAQ ──────────────────────────────────────────────────────────────────────
const FAQ = () => {
  const [open, setOpen] = useState(null);

  return (
    <section id="faq" className="section-pad">
      <div className="max-container max-w-3xl">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-3">
            FAQ
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white">
            Common <span className="gradient-text">questions</span>
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <Card key={i} className="overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="flex items-center justify-between w-full p-5 text-left"
              >
                <span className="font-semibold text-gray-900 dark:text-white pr-4">
                  {faq.q}
                </span>
                {open === i ? (
                  <ChevronUp
                    size={18}
                    className="flex-shrink-0 text-primary-500"
                  />
                ) : (
                  <ChevronDown
                    size={18}
                    className="flex-shrink-0 text-gray-400"
                  />
                )}
              </button>
              {open === i && (
                <div className="px-5 pb-5 text-sm text-gray-500 dark:text-gray-400 leading-relaxed border-t border-gray-100 dark:border-gray-700 pt-4 animate-fade-in">
                  {faq.a}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── CTA Banner ───────────────────────────────────────────────────────────────
const CTA = () => {
  const navigate = useNavigate();
  return (
    <section className="section-pad">
      <div className="max-container">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 via-violet-600 to-accent-500 p-12 text-center">
          <div className="orb w-64 h-64 bg-white/20 top-0 -right-20 blur-3xl" />
          <div className="orb w-64 h-64 bg-white/10 bottom-0 -left-20 blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-4">
              Ready to move faster?
            </h2>
            <p className="text-primary-100 text-lg mb-8 max-w-xl mx-auto">
              Join 1,780+ teams already using NexaFlow. Start your free 14-day
              trial — no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="subtle"
                size="lg"
                className="!bg-white !text-primary-700 hover:!bg-primary-50"
                onClick={() => navigate("/signup")}
              >
                Start Free Trial
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className="!border-white/40 !text-white hover:!bg-white/10"
                onClick={() => navigate("/dashboard")}
              >
                View Demo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Landing Page (default export) ───────────────────────────────────────────
const Landing = () => (
  <div className="min-h-screen">
    <Navbar />
    <Hero />
    <Features />
    <Pricing />
    <Testimonials />
    <FAQ />
    <CTA />
    <Footer />
  </div>
);

export default Landing;
